/**
 * O motor de ordenação por coluna (canônico) — a metade sem JSX.
 *
 * Nasceu no pacote UI/UX do monitoramento (10/09/2026) como o PRIMEIRO motor
 * de ordenação por clique do sistema. O cabeçalho clicável que o usa é o
 * `<ThOrdenavel>` (components/ui) — hook e sort vivem AQUI porque um arquivo
 * de componente só pode exportar componentes (react-refresh).
 *
 * Regras que ele carrega:
 *   · clicar na coluna ativa inverte o sentido; noutra coluna, começa em `asc`;
 *   · vazio (null/undefined/'') vai SEMPRE para o fim, nos dois sentidos —
 *     ordenar por "Tipo" não pode afundar a lista útil sob os sem-tipo;
 *   · texto compara em pt-BR com `numeric` ("câmera 2" antes de "câmera 10");
 *   · o estado é React state — preferência de sessão, nunca localStorage.
 */
import { useState } from 'react';

export type SentidoDaOrdem = 'asc' | 'desc';

export interface Ordenacao<K extends string> {
  chave: K;
  sentido: SentidoDaOrdem;
  alternar: (nova: K) => void;
}

export function useOrdenacao<K extends string>(
  padrao: K,
  sentidoPadrao: SentidoDaOrdem = 'asc'
): Ordenacao<K> {
  const [chave, setChave] = useState<K>(padrao);
  const [sentido, setSentido] = useState<SentidoDaOrdem>(sentidoPadrao);
  return {
    chave,
    sentido,
    alternar: (nova: K) => {
      if (nova === chave) {
        setSentido((s) => (s === 'asc' ? 'desc' : 'asc'));
      } else {
        setChave(nova);
        setSentido('asc');
      }
    },
  };
}

/** Ordena SEM mutar a lista original (o dado vem do react-query — é congelado). */
export function ordenarLista<T>(
  lista: T[],
  valor: (item: T) => string | number | null | undefined,
  sentido: SentidoDaOrdem
): T[] {
  const fator = sentido === 'asc' ? 1 : -1;
  return [...lista].sort((a, b) => {
    const va = valor(a);
    const vb = valor(b);
    const vaVazio = va == null || va === '';
    const vbVazio = vb == null || vb === '';
    if (vaVazio && vbVazio) return 0;
    if (vaVazio) return 1;
    if (vbVazio) return -1;
    if (typeof va === 'number' && typeof vb === 'number') return (va - vb) * fator;
    return String(va).localeCompare(String(vb), 'pt-BR', { numeric: true, sensitivity: 'base' }) * fator;
  });
}
